import { useEffect, useRef, useState } from "react";
import { Languages, Loader2, Send } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import type { CollaborationMessage } from "@/hooks/useCollaborations";

/**
 * The conversation between the two parties.
 *
 * Translation is per message and on demand rather than eager: it costs money
 * per call and is cached server-side per target language, so a message is paid
 * for once no matter how often the thread is re-rendered. It earns its keep the
 * moment MX and CO land alongside a Brazilian creator.
 */
export default function CollaborationChat({
  messages,
  onSend,
  onTranslate,
  onRead,
  busy,
}: {
  messages: CollaborationMessage[];
  onSend: (content: string) => Promise<void>;
  onTranslate: (messageId: string, language: string) => Promise<string>;
  onRead: () => void;
  busy: boolean;
}) {
  const { t, currentLanguage } = useTranslation();
  const { user } = useAuth();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Marking read is the side effect of having the thread on screen, so it
  // belongs to mounting it rather than to any particular click.
  useEffect(() => {
    if (messages.length > 0) onRead();
    // Only when the thread itself changes — re-running on every render would
    // fire a mutation per keystroke in the composer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const send = async () => {
    const content = draft.trim();
    if (!content) return;

    setError(null);
    // Cleared optimistically: the message almost always sends, and retyping a
    // paragraph because a refetch was slow is the worse failure.
    setDraft("");
    try {
      await onSend(content);
    } catch (e) {
      setDraft(content);
      setError(e instanceof Error ? e.message : t("marketplace.collaboration.chat.failed"));
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-8">
            {t("marketplace.collaboration.chat.empty")}
          </p>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              mine={message.senderId === user?.id}
              targetLanguage={currentLanguage}
              onTranslate={onTranslate}
            />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-control px-3 py-2 mt-2">
          {error}
        </p>
      )}

      <div className="flex items-end gap-2 pt-3 mt-3 border-t border-neutral-100">
        <textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter breaks the line — what every messaging
            // surface these users already have does.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder={t("marketplace.collaboration.chat.placeholder")}
          className="flex-1 min-w-0 px-3 py-2 rounded-control border border-neutral-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600"
        />
        <button
          type="button"
          disabled={busy || !draft.trim()}
          onClick={send}
          className="p-2.5 rounded-button bg-gradient-to-r from-steel-600 to-steel-700 text-white hover:from-steel-500 hover:to-steel-600 transition-all cursor-pointer disabled:opacity-40 flex-shrink-0"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  mine,
  targetLanguage,
  onTranslate,
}: {
  message: CollaborationMessage;
  mine: boolean;
  targetLanguage: string;
  onTranslate: (messageId: string, language: string) => Promise<string>;
}) {
  const { t } = useTranslation();
  const [translation, setTranslation] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);

  const translate = async () => {
    setTranslating(true);
    try {
      setTranslation(await onTranslate(message.id, targetLanguage));
    } catch {
      setTranslation(null);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] min-w-0 ${mine ? "items-end" : "items-start"}`}>
        <div
          className={`rounded-card px-3 py-2 ${
            mine
              ? "bg-steel-600 text-white"
              : "bg-white border border-neutral-200 text-neutral-900"
          }`}
        >
          {!mine && message.senderName && (
            <p className="text-[10px] font-semibold text-neutral-400 mb-0.5">
              {message.senderName}
            </p>
          )}
          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
            {message.content}
          </p>

          {translation && (
            <p
              className={`text-sm leading-relaxed whitespace-pre-wrap break-words mt-2 pt-2 border-t ${
                mine ? "border-white/20 text-white/90" : "border-neutral-100 text-neutral-600"
              }`}
            >
              {translation}
            </p>
          )}
        </div>

        <div className={`flex items-center gap-2 mt-1 ${mine ? "justify-end" : ""}`}>
          <span className="text-[10px] text-neutral-400">
            {new Date(message.createdAt).toLocaleTimeString("es", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>

          {!translation && (
            <button
              type="button"
              onClick={translate}
              disabled={translating}
              className="inline-flex items-center gap-1 text-[10px] text-neutral-400 hover:text-steel-600 transition-colors cursor-pointer disabled:opacity-40"
            >
              {translating ? (
                <Loader2 className="w-2.5 h-2.5 animate-spin" />
              ) : (
                <Languages className="w-2.5 h-2.5" />
              )}
              {t("marketplace.collaboration.chat.translate")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
